import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { AppConfig } from '~/core/models/appconfig';

// Priority order: Try browser/assets/config.json first (deployment path for all envs)
// Fallback to assets/config.json if browser path doesn't exist
const FILES = ["browser/assets/config.json", "assets/config.json"];
const LOCAL_FILE = 'assets/setlocal.json';

@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    private config: AppConfig = new AppConfig();
    private http: HttpClient;

    constructor(private httpHandler: HttpBackend) {
        this.http = new HttpClient(this.httpHandler);
    }

    public async init(): Promise<boolean> {
        try {
            let configData: AppConfig | null = null;

            // Try each config file path until one succeeds
            // Priority: browser/assets/config.json (deployment), then assets/config.json (dev fallback)
            for (const file of FILES) {
                try {
                    const config$ = this.http.get<AppConfig>(file);
                    configData = await lastValueFrom(config$);
                    break; // Success, exit loop
                } catch (e) {
                    // Failed to load from this path, trying next
                }
            }

            if (!configData) {
                throw new Error('Failed to load config from any path');
            }

            // Try to load local override config (optional, for local dev)
            let localConfig: Partial<AppConfig> | null = null;
            try {
                const local$ = this.http.get<Partial<AppConfig>>(LOCAL_FILE);
                localConfig = await lastValueFrom(local$);
            } catch (e) {
                // Local config is optional
            }

            // Merge local config if available
            this.config = { ...configData, ...(localConfig || {}) };

            return true;
        } catch (error) {
            throw error;
        }
    }

    public getConfig(key?: string | string[]): any {
        if (!key || (Array.isArray(key) && !key[0])) {
            return this.config;
        }

        const path = Array.isArray(key) ? key : key.split('.');
        return path.reduce((acc: any, current: string) => acc?.[current], this.config);
    }

    /**
     * Get API URL with fallback logic
     * Returns config.apiUrl if available, or attempts to get from window config
     */
    public getApiUrl(): string {
        if (this.config.apiUrl) {
            return this.config.apiUrl;
        }
        
        // Fallback: try to get from window or environment
        if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.apiUrl) {
            return (window as any).APP_CONFIG.apiUrl;
        }
        
        return '';
    }

    /**
     * Get Outbound API URL with fallback logic
     */
    public getOutboundApiUrl(): string {
        // if (this.config.outboundApiUrl) {
        //   return this.config.outboundApiUrl;
        // }
        
        if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.outboundApiUrl) {
            return (window as any).APP_CONFIG.outboundApiUrl;
        }
        
        return '';
    }

    /**
     * Get Base URL with fallback logic
     */
    public getBaseUrl(): string {
        if (this.config.baseUrl) {
            return this.config.baseUrl;
        }
        
        if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.baseUrl) {
            return (window as any).APP_CONFIG.baseUrl;
        }
        
        // Fallback to current window location
        return window?.location?.origin || '';
    }

    /**
     * Get Wells API Key with fallback logic
     */
    public getWellsApiKey(): string {
        if (this.config.wellsApiKey) {
            return this.config.wellsApiKey;
        }
        
        if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.wellsApiKey) {
            return (window as any).APP_CONFIG.wellsApiKey;
        }
        
        return '';
    }

    /**
     * Safely concatenate base URL with endpoint, ensuring only one slash between them
     * @param baseUrl - The base URL (e.g., "http://localhost:8000" or "http://localhost:8000/")
     * @param endpoint - The endpoint path (e.g., "/api/v1/endpoint" or "api/v1/endpoint")
     * @returns Properly formatted URL with exactly one slash separator
     */
    public concatenateUrl(baseUrl: string, endpoint: string): string {
        if (!baseUrl || !endpoint) {
            return (baseUrl + endpoint).trim();
        }

        // Remove trailing slashes from baseUrl
        const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
        // Ensure endpoint starts with a single slash
        const cleanEndpoint = ('/' + endpoint).replace(/\/+/, '/');
        
        return cleanBaseUrl + cleanEndpoint;
    }
}